import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Box,
  AppBar,
  Toolbar,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  Avatar,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Category as CategoryIcon,
  Search as SearchIcon,
  Image as ImageIcon,
  Label as LabelIcon,
  Storefront as StorefrontIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import adminAuthService from '../services/adminAuthService';
import { API_BASE_URL } from '../config/api';

const CategoryManagement = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [currentBrand, setCurrentBrand] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0 for categories, 1 for brands
  const [formData, setFormData] = useState({
    name: '',
    image_url: ''
  });

  // Statistics state
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0
  });

  const [brandStats, setBrandStats] = useState({
    total: 0,
    active: 0,
    inactive: 0
  });

  // Helper function to format image URLs
  const formatImageUrl = (imageUrl, type = 'categories') => {
    if (!imageUrl) return '';
    
    // If it's a blob URL, don't process it
    if (imageUrl.startsWith('blob:')) {
      return '';
    }
    
    // If it's already a full URL, return as is
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    
    // If it's just a filename, construct the full URL
    return `https://al-sadik.com/api/uploads/${type}/${imageUrl}`;
  };

  useEffect(() => {
    loadCategories();
    loadBrands();
  }, []);

  useEffect(() => {
    filterCategories();
  }, [categories, searchQuery]);

  useEffect(() => {
    filterBrands();
  }, [brands, searchQuery]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/admin_categories.php`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminAuthService.token}`,
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setCategories(data.categories || []);
        calculateStats(data.categories || []);
      } else {
        setError(data.message || 'فشل في تحميل بيانات الفئات');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال بالخادم');
    } finally {
      setLoading(false);
    }
  };

  const loadBrands = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin_brands.php`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminAuthService.token}`,
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setBrands(data.brands || []);
        calculateBrandStats(data.brands || []);
      } else {
        setError(data.message || 'فشل في تحميل بيانات العلامات التجارية');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال بالخادم');
    }
  };

  const calculateStats = (categoriesList) => {
    const total = categoriesList.length;
    // Since we don't have status field, we'll show all as active
    const active = categoriesList.length;
    const inactive = 0;

    setStats({ total, active, inactive });
  };

  const calculateBrandStats = (brandsList) => {
    const total = brandsList.length;
    // Since we don't have status field, we'll show all as active
    const active = brandsList.length;
    const inactive = 0;

    setBrandStats({ total, active, inactive });
  };

  const filterCategories = () => {
    let filtered = categories;

    if (searchQuery) {
      filtered = filtered.filter(category =>
        category.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredCategories(filtered);
  };

  const filterBrands = () => {
    let filtered = brands;

    if (searchQuery) {
      filtered = filtered.filter(brand =>
        brand.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredBrands(filtered);
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      if (activeTab === 0) {
        // Category
        setCurrentCategory(item);
        setCurrentBrand(null);
      } else {
        // Brand
        setCurrentBrand(item);
        setCurrentCategory(null);
      }
      setFormData({
        name: item.name || '',
        image_url: item.image_url || ''
      });
    } else {
      setCurrentCategory(null);
      setCurrentBrand(null);
      setFormData({
        name: '',
        image_url: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentCategory(null);
    setCurrentBrand(null);
    setFormData({
      name: '',
      image_url: ''
    });
  };

  const handleSave = async () => {
    if (saving) return; // Prevent double submission

    const isCategory = activeTab === 0;
    const currentItem = isCategory ? currentCategory : currentBrand;
    const endpoint = isCategory ? 'admin_categories.php' : 'admin_brands.php';
    const itemType = isCategory ? 'الفئة' : 'العلامة التجارية';

    try {
      setSaving(true);
      const requestData = {
        action: currentItem ? 'update' : 'create',
        ...formData
      };

      if (currentItem) {
        requestData[isCategory ? 'category_id' : 'brand_id'] = currentItem.id;
      }

      const response = await fetch(`${API_BASE_URL}/api/${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(currentItem ? `تم تحديث ${itemType} بنجاح` : `تم إضافة ${itemType} بنجاح`);
        handleCloseDialog();
        if (isCategory) {
          loadCategories();
        } else {
          loadBrands();
        }
      } else {
        setError(data.message || `فشل في حفظ بيانات ${itemType}`);
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال بالخادم');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (itemId) => {
    const isCategory = activeTab === 0;
    const endpoint = isCategory ? 'admin_categories.php' : 'admin_brands.php';
    const itemType = isCategory ? 'الفئة' : 'العلامة التجارية';
    
    if (!window.confirm(`هل أنت متأكد من حذف ${itemType}؟ سيؤثر هذا على جميع المنتجات المرتبطة بها.`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'delete',
          [isCategory ? 'category_id' : 'brand_id']: itemId
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(`تم حذف ${itemType} بنجاح`);
        if (isCategory) {
          loadCategories();
        } else {
          loadBrands();
        }
      } else {
        setError(data.message || `فشل في حذف ${itemType}`);
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال بالخادم');
    }
  };

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('يجب اختيار ملف صورة صالح');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
      return;
    }

    const uploadFormData = new FormData();
    uploadFormData.append('image', file);
    uploadFormData.append('type', activeTab === 0 ? 'categories' : 'brands'); // Specify upload type

    try {
      setUploading(true);
      
      const uploadUrl = `${API_BASE_URL}/api/upload_image.php`;
      
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 30000); // 30 second timeout
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.token}`
        },
        body: uploadFormData,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const responseText = await response.text();
      
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = JSON.parse(responseText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          errorMessage += `\nRaw response: ${responseText}`;
        }
        throw new Error(errorMessage);
      }

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error('Server returned invalid JSON response: ' + responseText);
      }

      if (result.success) {
        const imageUrl = result.url || result.filename;
        
        setFormData(prev => ({
          ...prev,
          image_url: imageUrl
        }));
        setSuccess('تم رفع الصورة بنجاح');
      } else {
        throw new Error(result.message || 'فشل في رفع الصورة');
      }
    } catch (error) {
      let errorMessage = 'خطأ في رفع الصورة: ';
      
      if (error.name === 'AbortError') {
        errorMessage += 'انتهت مهلة الرفع (30 ثانية). حاول مرة أخرى أو استخدم صورة أصغر.';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage += 'فشل في الاتصال بالخادم. تحقق من اتصالك بالإنترنت.';
      } else {
        errorMessage += error.message;
      }
      
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  if (!adminAuthService.isAuthenticated()) {
    navigate('/admin/login');
    return null;
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Navigation Bar */}
      <AppBar position="static" sx={{ backgroundColor: '#4169E1' }}>
        <Toolbar>
          <IconButton
            color="inherit"
            onClick={() => navigate('/admin/dashboard')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <CategoryIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            إدارة الفئات والعلامات التجارية
          </Typography>
          <Button
            color="inherit"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            {activeTab === 0 ? 'إضافة فئة جديدة' : 'إضافة علامة تجارية جديدة'}
          </Button>
        </Toolbar>
      </AppBar>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} aria-label="category brand tabs">
          <Tab label="الفئات" icon={<CategoryIcon />} />
          <Tab label="العلامات التجارية" icon={<StorefrontIcon />} />
        </Tabs>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Page Header */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4169E1', mb: 1 }}>
            إدارة الفئات والعلامات التجارية
          </Typography>
          <Typography variant="body1" color="text.secondary">
            يمكنك من هنا إضافة وتعديل وحذف فئات المنتجات والعلامات التجارية
          </Typography>
        </Paper>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                {activeTab === 0 ? <CategoryIcon sx={{ fontSize: 40, color: '#4169E1', mb: 1 }} /> : <StorefrontIcon sx={{ fontSize: 40, color: '#4169E1', mb: 1 }} />}
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {activeTab === 0 ? stats.total : brandStats.total}
                </Typography>
                <Typography color="text.secondary">
                  {activeTab === 0 ? 'إجمالي الفئات' : 'إجمالي العلامات التجارية'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <LabelIcon sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {activeTab === 0 ? stats.active : brandStats.active}
                </Typography>
                <Typography color="text.secondary">
                  {activeTab === 0 ? 'فئات نشطة' : 'علامات تجارية نشطة'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <ImageIcon sx={{ fontSize: 40, color: '#ff9800', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {activeTab === 0 ? 
                    categories.filter(c => c.image_url).length : 
                    brands.filter(b => b.image_url).length
                  }
                </Typography>
                <Typography color="text.secondary">
                  {activeTab === 0 ? 'فئات لها صور' : 'علامات تجارية لها صور'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Search */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={activeTab === 0 ? "البحث في الفئات..." : "البحث في العلامات التجارية..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        {/* Categories/Brands Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>الصورة</TableCell>
                  <TableCell>الاسم</TableCell>
                  <TableCell>عدد المنتجات</TableCell>
                  <TableCell align="center">الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      جاري التحميل...
                    </TableCell>
                  </TableRow>
                ) : (activeTab === 0 ? filteredCategories : filteredBrands).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      {activeTab === 0 ? 'لا توجد فئات' : 'لا توجد علامات تجارية'}
                    </TableCell>
                  </TableRow>
                ) : (
                  (activeTab === 0 ? filteredCategories : filteredBrands).map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Avatar
                          src={formatImageUrl(item.image_url, activeTab === 0 ? 'categories' : 'brands')}
                          alt={item.name}
                          sx={{ width: 50, height: 50 }}
                        >
                          {activeTab === 0 ? <CategoryIcon /> : <StorefrontIcon />}
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {item.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${item.products_count || 0} منتج`}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(item)}
                          title="تعديل"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(item.id)}
                          title="حذف"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {activeTab === 0 ? 
            (currentCategory ? 'تعديل الفئة' : 'إضافة فئة جديدة') :
            (currentBrand ? 'تعديل العلامة التجارية' : 'إضافة علامة تجارية جديدة')
          }
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={activeTab === 0 ? "اسم الفئة" : "اسم العلامة التجارية"}
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleInputChange('name')}
            sx={{ mb: 2 }}
            required
          />
          
          {/* Image Upload */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {activeTab === 0 ? 'صورة الفئة' : 'صورة العلامة التجارية'}
            </Typography>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="image-upload"
              type="file"
              onChange={handleImageUpload}
            />
            <label htmlFor="image-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<ImageIcon />}
                sx={{ mb: 1 }}
                disabled={uploading}
              >
                {uploading ? 'جاري الرفع...' : 'اختر صورة'}
              </Button>
            </label>
            {formData.image_url && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar 
                  src={formatImageUrl(formData.image_url, activeTab === 0 ? 'categories' : 'brands')} 
                  sx={{ width: 50, height: 50 }}
                >
                  <ImageIcon />
                </Avatar>
                <Typography variant="body2" color="text.secondary">
                  تم رفع الصورة بنجاح
                </Typography>
                <Button
                  size="small"
                  onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                  color="error"
                >
                  إزالة
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>إلغاء</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={saving}
          >
            {saving ? 'جاري الحفظ...' : 
              (activeTab === 0 ? 
                (currentCategory ? 'تحديث' : 'إضافة') :
                (currentBrand ? 'تحديث' : 'إضافة')
              )
            }
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryManagement;
